'use client';

import LoadingPage from '@/components/LoadingPage';
import { useCurrentUser } from '@/hooks/use-current-user';
import profileService from '@/services/profileService';
import { isUserRealtor } from '@/utils/profile';
import { RealtorFields, UserProfile } from '@/utils/type';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { User } from 'firebase/auth';
import { useRouter } from 'next/navigation';
import React, { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';


// Add the AddressFields interface
export interface AddressFields {
  street1: string;
  street2: string;
  unit: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
}
// Validation function for required fields
export const validateProfileFields = (
  firstName: string,
  lastName: string,
  phoneNumber: string,
  addressFields: AddressFields
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Validate first name
  if (!firstName.trim()) {
    errors.push('First name is required');
  }

  // Validate last name
  if (!lastName.trim()) {
    errors.push('Last name is required');
  }

  // Validate phone number (basic validation)
  if (!phoneNumber || phoneNumber.length < 10) {
    errors.push('Valid phone number is required');
  }

  // Validate required address fields
  if (!addressFields.street1.trim()) {
    errors.push('Street address is required');
  }
  if (!addressFields.city.trim()) {
    errors.push('City is required');
  }
  if (!addressFields.province.trim()) {
    errors.push('Province/State is required');
  }
  if (!addressFields.country.trim()) {
    errors.push('Country is required');
  }
  if (!addressFields.postalCode.trim()) {
    errors.push('Postal/ZIP code is required');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}
type ProfileContextType = {
  currentUser: User | null;
  firstName: string;
  setFirstName: Dispatch<SetStateAction<string>>;
  lastName: string;
  setLastName: Dispatch<SetStateAction<string>>;
  displayName: string;
  phoneNumber: string | number | any;
  setPhoneNumber: Dispatch<SetStateAction<string>>;
  address: string; // Keeps JSON string for backward compatibility
  setAddress: Dispatch<SetStateAction<string>>;
  addressFields: AddressFields; // New structured address fields
  setAddressFields: Dispatch<SetStateAction<AddressFields>>;
  userRole: string | null;
  selectedUserRole: string | null;
  setSelectedUserRole: Dispatch<SetStateAction<string | null>>;
  progress: number;
  previousProfileInfo: boolean;
  isLoading: boolean;
  showSuccess: boolean;
  isProfileComplete: boolean;
  isUnderReview: boolean;
  missingInformation: string | null;
  handleComplete: () => Promise<void>;
  isSidebarCollapsed: boolean;
  status: 'approved' | 'rejected' | 'pending' | undefined;
  rejectionReason: string | null;
  realtorFields: RealtorFields;
  setRealtorFields: Dispatch<SetStateAction<RealtorFields>>;
  userProfile: UserProfile | null;
};

const ProfileContext = createContext<ProfileContextType | null>(null);

const defaultAddressFields: AddressFields = {
  street1: '',
  street2: '',
  unit: '',
  city: '',
  province: '',
  country: '',
  postalCode: '',
};
export const ProfileContextProvider = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter();
  const [currentUser, authStatus] = useCurrentUser();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [address, setAddress] = useState('');
  const [addressFields, setAddressFields] = useState<AddressFields>(defaultAddressFields);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [selectedUserRole, setSelectedUserRole] = useState<string | null>(null);
  const [progress, setProgress] = useState(10);
  const [previousProfileInfo, setPreviousProfileInfo] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const [isUnderReview, setIsUnderReview] = useState(false);
  const [missingInformation, setMissingInformation] = useState<string | null>(null);
  const isSidebarCollapsed = Boolean(useMediaQuery(`(max-width: 1024px)`));
  const [status, setStatus] = useState<'approved' | 'rejected' | 'pending' | undefined>(undefined);
  const [rejectionReason, setRejectionReason] = useState<string | null>(null);
  const [realtorFields, setRealtorFields] = useState<RealtorFields>({
    brokerageName: '',
    additionalEmails: [],
  });
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  const displayName = firstName || lastName ? `${firstName} ${lastName}`.trim() : currentUser?.displayName || '';

  const setDefault = () => {
    setFirstName('');
    setLastName('');
    setPhoneNumber('');
    setAddress('');
    setAddressFields(defaultAddressFields);
    setUserRole('General User');
    setIsUnderReview(false);
    setMissingInformation(null);
    setProgress(10);
    setPreviousProfileInfo(false);
  };

  // Parse address string to addressFields when address is updated externally
  useEffect(() => {
    try {
      const parsedAddress = JSON.parse(address);
      if (typeof parsedAddress === 'object' && parsedAddress !== null) {
        setAddressFields({
          street1: parsedAddress.street1 || '',
          street2: parsedAddress.street2 || '',
          unit: parsedAddress.unit || '',
          city: parsedAddress.city || '',
          province: parsedAddress.province || '',
          country: parsedAddress.country || '',
          postalCode: parsedAddress.postalCode || '',
        });
      }
    } catch {
      // If address isn't valid JSON, keep existing addressFields
    }
  }, [address]);

  useEffect(() => {
    if (authStatus === 'logged-out') {
      router.push("/");
    }
  }, [authStatus, router]);

  // First, update the useEffect that handles fetching user data to properly parse the address:
  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser?.email) {
        try {
          const userData = await profileService.fetchUserProfileByEmail(currentUser.email as string);
          if (userData?.email) {
            setUserProfile(userData);
            setFirstName(userData.firstName || '');
            setLastName(userData.lastName || '');
            setPhoneNumber(userData.phoneNumber || '');

            // Try to parse the address as structured data first
            try {
              const parsedAddress = JSON.parse(userData.address || '{}');
              if (typeof parsedAddress === 'object' && parsedAddress !== null) {
                setAddressFields({
                  street1: parsedAddress.street1 || '',
                  street2: parsedAddress.street2 || '',
                  unit: parsedAddress.unit || '',
                  city: parsedAddress.city || '',
                  province: parsedAddress.province || '',
                  country: parsedAddress.country || '',
                  postalCode: parsedAddress.postalCode || '',
                });
              }
            } catch {
              // If address isn't valid JSON, try to parse it as a comma-separated string
              const addressParts = (userData.address || '').split(',').map(part => part.trim());
              setAddressFields({
                street1: addressParts[0] || '',
                street2: addressParts[2] || '',
                unit: (addressParts[1] || '').replace('Unit ', '') || '',
                city: addressParts[3] || '',
                province: addressParts[4] || '',
                country: addressParts[5] || '',
                postalCode: addressParts[6] || '',
              });
            }

            setUserRole(userData.userRole || 'General User');
            setIsUnderReview(userData.isUnderReview || false);
            setMissingInformation(userData.missingInformation || null);
            setProgress(userData.previousProfileInfo ? 100 : 10);
            setPreviousProfileInfo(userData.previousProfileInfo as boolean);
            setStatus(userData.status as any);
            setRejectionReason(userData.rejectionReason ?? null);

            if (userData.realtorFields) {
              setRealtorFields({
                brokerageName: userData.realtorFields.brokerageName || '',
                additionalEmails: Array.isArray(userData.realtorFields.additionalEmails)
                  ? userData.realtorFields.additionalEmails
                  : [],
              });
            }
          } else {
            setDefault();
          }
        } catch (error) {
          setDefault();
          console.error('Error fetching user data:', error);
        }
      }
    };

    fetchUserData();
  }, [currentUser]);

  // Update the formatAddressForDisplay function to handle empty fields better:
  const formatAddressForDisplay = (addressFields: AddressFields): string => {
    const parts = [
      addressFields.street1,
      addressFields.unit && `Unit ${addressFields.unit}`,
      addressFields.street2,
      addressFields.city,
      addressFields.province,
      addressFields.country,
      addressFields.postalCode
    ];

    // Filter out empty strings, undefined, and null values
    const validParts = parts.filter(part => part && part.trim().length > 0);

    return validParts.join(', ');
  };

  // Add a useEffect to update formatted address whenever addressFields changes
  useEffect(() => {
    const formatted = formatAddressForDisplay(addressFields);
    setAddress(formatted);
  }, [addressFields]);

  // Update the handleComplete function to properly handle address:
  const handleComplete = async () => {
    if (!currentUser?.uid) {
      console.error('No current user UID available');
      return;
    }

    // Validate required fields
    const validation = validateProfileFields(
      firstName,
      lastName,
      phoneNumber,
      addressFields
    );

    if (!validation.isValid) {
      // Show validation errors using Mantine's notifications
      validation.errors.forEach(error => {
        notifications.show({
          title: 'Error',
          message: error,
          color: 'red'
        });
      });
      return;
    }

    setIsLoading(true);
    try {
      // Handle role selection and changes
      const submittedUserRole = selectedUserRole || userRole || 'General User';
      const isRoleChange = previousProfileInfo && userRole !== submittedUserRole;

      const formattedAddress = formatAddressForDisplay(addressFields);
      const addressJSON = JSON.stringify(addressFields);

      const userData = {
        userId: currentUser.uid,
        firstName,
        lastName,
        phoneNumber,
        address: addressJSON,
        addressString: formattedAddress,
        userRole: submittedUserRole,
        email: currentUser.email,
        previousProfileInfo: true,
        isUnderReview: isUserRealtor(submittedUserRole),
        status: status === 'rejected' ? 'pending' : status,
        ...(isUserRealtor(submittedUserRole) && { realtorFields }),
      };
      // console.log("userData Profile Se", userData)
      await profileService.updateUserProfile(currentUser.uid, userData);

      // Handle role change request if applicable
      const shouldCreateRoleRequest = (
        isUserRealtor(submittedUserRole) || // New Realtor role
        (isRoleChange && submittedUserRole !== 'General User') || // Any non-General role change
        (status === 'rejected' && isUserRealtor(userRole)) // Resubmitting after rejection
      );

      if (shouldCreateRoleRequest) {
        // console.log("shouldCreateRoleRequest", shouldCreateRoleRequest)
        await profileService.submitRoleChangeRequest(
          currentUser.uid,
          userData,
          submittedUserRole as 'Realtor',
          formattedAddress,
          currentUser.email as string,
          status === 'rejected',
          rejectionReason ?? null,
        );
      }

      // Update state after successful submission
      setUserRole(submittedUserRole);
      setProgress(100);
      setPreviousProfileInfo(true);
      setShowSuccess(true);
      setIsUnderReview(isUserRealtor(submittedUserRole));
      setStatus(prev => prev === 'rejected' ? 'pending' : prev);
      setRejectionReason(null);

      // Show success notification
      notifications.show({
        title: 'Success',
        message: 'Profile updated successfully',
        color: 'green'
      });

    } catch (error) {
      console.error('Error updating user data:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update profile. Please try again.',
        color: 'red'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const validation = validateProfileFields(
      firstName,
      lastName,
      phoneNumber,
      addressFields
    );

    // Set progress based on filled required fields
    let calculatedProgress = currentUser?.emailVerified ? 10 : 0;

    // Calculate progress based on required fields only
    const totalRequiredFields = 4; // firstName, lastName, phoneNumber, address
    let filledRequiredFields = 0;

    if (firstName) filledRequiredFields++;
    if (lastName) filledRequiredFields++;
    if (phoneNumber) filledRequiredFields++;
    if (Object.values(addressFields).some(value => value !== '')) filledRequiredFields++;

    calculatedProgress += (filledRequiredFields / totalRequiredFields) * 90;

    setProgress(calculatedProgress);
    setIsProfileComplete(validation.isValid);
  }, [firstName, lastName, phoneNumber, addressFields, currentUser?.emailVerified]);
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (showSuccess) {
      timeoutId = setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [showSuccess]);
  const formattedAddress = formatAddressForDisplay(addressFields);
  // console.log("formattedAddress", formattedAddress)
  return (
    <ProfileContext.Provider
      value={{
        currentUser,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        displayName,
        phoneNumber,
        setPhoneNumber,
        address: formattedAddress,
        setAddress,
        addressFields,
        setAddressFields,
        userRole,
        selectedUserRole,
        setSelectedUserRole,
        progress,
        previousProfileInfo,
        isLoading,
        showSuccess,
        isProfileComplete,
        isUnderReview,
        missingInformation,
        handleComplete,
        isSidebarCollapsed,
        status,
        rejectionReason,
        realtorFields,
        setRealtorFields,
        userProfile,
      }}>
      {authStatus === 'pending' && <LoadingPage />}
      {authStatus === 'logged-in' && <>{children}</>}
    </ProfileContext.Provider>
  );
};

export const useProfileContext = () => {
  const profileContext = useContext(ProfileContext);
  if (profileContext === null) {
    throw new Error("Must use inside profile context provider.");
  }

  return profileContext;
}